"use client";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Card, IconButton, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBadge from "@/components/atoms/MDBadge";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { IConversationTransferStep } from "@/interfaces/conversation-transfer.interfaces";
import usePopOver from "@/hooks/use-popover";
import pxToRem from "@/themes/functions/pxToRem";

import { CurrentChatPopOver } from "./CurrentChatPopOver";

type Props = {
  contactName: string;
  chatInputEnabled?: boolean;
  canAssume?: boolean;
  canFinish?: boolean;
  disableChatInput?: () => void;
  enableChatInput?: () => void;
  onBack?: () => void;
  transferSteps?: IConversationTransferStep[];
  onTransferToStep?: (step: IConversationTransferStep) => void;
};

function ChatHeader({
  contactName,
  chatInputEnabled = false,
  canAssume = false,
  canFinish = false,
  disableChatInput,
  enableChatInput,
  onBack,
  transferSteps = [],
  onTransferToStep,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { openPopover, anchorEl, closePopover, isOpen } = usePopOver();

  const hasControllers =
    !!enableChatInput &&
    !!disableChatInput &&
    (canAssume || canFinish || transferSteps.length > 0);

  return (
    <Card
      sx={(theme) => ({
        backgroundColor: theme.palette.primary.light,
        borderRadius: theme.borders.borderRadius.xl,
        minHeight: isMobile ? pxToRem(56) : pxToRem(85),
      })}
    >
      <MDBox p={isMobile ? 1.5 : 2}>
        <Grid container spacing={isMobile ? 1 : 3} alignItems="center">
          {isMobile && onBack && (
            <Grid>
              <IconButton onClick={onBack} sx={{ color: "white", p: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Grid>
          )}
          <Grid>
            <MDBox height="100%" lineHeight={1}>
              <MDTypography color="white" variant={isMobile ? "h6" : "h5"} fontWeight="medium">
                {contactName}
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid sx={{ ml: "auto" }}>
            {hasControllers && (
              <>
                <IconButton onClick={(event) => openPopover(event, null)} sx={{ p: isMobile ? 0.5 : 1 }}>
                  <MDBadge color="primary">
                    <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
                  </MDBadge>
                </IconButton>
                <CurrentChatPopOver
                  isOpen={isOpen}
                  anchorEl={anchorEl}
                  onClose={closePopover}
                  canAssume={canAssume}
                  canFinish={canFinish}
                  chatInputEnabled={chatInputEnabled}
                  enableChatInput={enableChatInput!}
                  disableChatInput={disableChatInput!}
                  transferSteps={transferSteps}
                  onTransferToStep={(step) => onTransferToStep?.(step)}
                />
              </>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default ChatHeader;
